import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAgentAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PaymentIcon } from "../../components/profile/PaymentIcons";
import { WalletBrandLogo } from "../../components/profile/WalletBrandLogos";
import {
  Banknote,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Wallet,
  Receipt,
  Filter,
  CheckCircle2,
  Clock,
  XCircle,
  Star,
  ExternalLink,
  ShieldCheck,
  Zap,
  ChevronDown,
  Check,
} from "lucide-react";

/* ---- bKash / Nagad / Rocket method theming (brand colors) ---- */
const METHOD_THEME = {
  bkash:  { color: "#E2136B", deep: "#A40E50", label: "bKash",  hint: "Mobile Banking", logo: "/payment-logos/bkash.png" },
  nagad:  { color: "#EC1C24", deep: "#B5121A", label: "Nagad",  hint: "Mobile Banking", logo: "/payment-logos/nagad.png" },
  rocket: { color: "#8E2A85", deep: "#5F1C58", label: "Rocket", hint: "Mobile Banking", logo: "/payment-logos/rocket.svg" },
};

/* ---- Gateway provider theming (HRpay / Cowpay / WPay / StarPago) ---- */
const PROVIDER_THEME = {
  hrpay:    { tone: "#10b981", short: "HRpay",    tagline: "Bangladesh PSP · Tier 1" },
  cowpay:   { tone: "#f59e0b", short: "Cowpay",   tagline: "Cross-border PSP" },
  wpay:     { tone: "#3b82f6", short: "WPay",     tagline: "Instant settlement" },
  starpago: { tone: "#a855f7", short: "StarPago", tagline: "BD/INT PSP" },
};

const fmtBdt = (n) =>
  `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
};

const STATUS_PILL = {
  completed: { icon: CheckCircle2, label: "Completed", color: "#10b981" },
  pending:   { icon: Clock,        label: "Pending",   color: "#f59e0b" },
  failed:    { icon: XCircle,      label: "Failed",    color: "#ef4444" },
  rejected:  { icon: XCircle,      label: "Rejected",  color: "#ef4444" },
};

export default function BdtDeposit() {
  const { refresh } = useAgentAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const [gatewayId, setGatewayId] = useState("");
  const [method, setMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [gatewayDropdownOpen, setGatewayDropdownOpen] = useState(false);

  const load = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/agent/bdt-deposit/config"),
        api.get("/agent/bdt-deposits"),
      ]);
      setCfg(c.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load BDT deposit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default-select the first (recommended) gateway as soon as cfg is loaded.
  const defaultGatewayId = useMemo(() => {
    const gws = cfg?.gateways || [];
    if (!gws.length) return "";
    return (gws.find((g) => g.recommended) || gws[0]).id;
  }, [cfg]);

  const effectiveGatewayId = gatewayId || defaultGatewayId;

  const activeGateway = useMemo(
    () => (cfg?.gateways || []).find((g) => g.id === effectiveGatewayId),
    [cfg, effectiveGatewayId]
  );

  // Default-select first supported method whenever the gateway changes.
  const defaultMethod = useMemo(() => {
    if (!activeGateway) return "";
    return (activeGateway.methods || [])[0] || "";
  }, [activeGateway]);

  const effectiveMethod = (() => {
    if (method && activeGateway?.methods?.includes(method)) return method;
    return defaultMethod;
  })();

  const filteredHistory = useMemo(() => {
    if (!historyFilter) return history;
    return history.filter((h) => (h.gateway_method || h.channel) === historyFilter);
  }, [history, historyFilter]);

  const totalCredited = useMemo(
    () =>
      history
        .filter((h) => h.status === "completed")
        .reduce((s, h) => s + Number(h.credited_amount || h.amount || 0), 0),
    [history]
  );

  /* ---- amount limits: take stricter of cfg vs gateway ---- */
  const effectiveMin = Math.max(
    Number(cfg?.min_amount || 0),
    Number(activeGateway?.min_amount || 0)
  );
  const effectiveMax = (() => {
    const a = Number(cfg?.max_amount || 0);
    const b = Number(activeGateway?.max_amount || 0);
    if (a > 0 && b > 0) return Math.min(a, b);
    return a || b || 0;
  })();

  const canSubmit =
    !!effectiveGatewayId &&
    !!effectiveMethod &&
    Number(amount) >= (effectiveMin || 0) &&
    (effectiveMax === 0 || Number(amount) <= effectiveMax) &&
    !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/agent/bdt-deposit", {
        gateway_id: effectiveGatewayId,
        amount: Number(amount),
        method: effectiveMethod,
      });
      if (data?.redirect_url) {
        toast.success("Redirecting to gateway checkout…");
        try { await refresh?.(); } catch (_e) { /* non-blocking */ }
        window.location.href = data.redirect_url;
        return;
      }
      toast.success("Deposit submitted");
      navigate(`/agent/bdt-deposit/return?ref=${data?.deposit_id || ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deposit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-[60vh]"
        data-testid="agent-bdt-deposit-loading"
      >
        <Loader2 className="animate-spin text-emerald-400" size={28} />
      </div>
    );
  }

  if (!cfg?.enabled) {
    return (
      <div
        className="max-w-2xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-amber-200 flex items-start gap-3"
        data-testid="agent-bdt-deposit-disabled"
      >
        <AlertTriangle size={22} className="text-amber-300 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-bold mb-1">BDT Deposit is currently disabled</div>
          <div className="text-sm text-amber-100/85">
            Admin has temporarily disabled BDT top-ups. Please use USDT Deposit, or contact your admin.
          </div>
        </div>
      </div>
    );
  }

  if (!(cfg.gateways || []).length) {
    return (
      <div
        className="max-w-2xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-amber-200 flex items-start gap-3"
        data-testid="agent-bdt-deposit-no-gateways"
      >
        <AlertTriangle size={22} className="text-amber-300 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-bold mb-1">No payment gateways available</div>
          <div className="text-sm text-amber-100/85">
            HRpay / Cowpay / WPay / StarPago are not configured or enabled. Please contact admin.
          </div>
        </div>
      </div>
    );
  }

  const activeProviderTheme =
    PROVIDER_THEME[activeGateway?.provider] || { tone: "#10b981", short: activeGateway?.provider, tagline: "" };

  return (
    <div className="max-w-[1280px] mx-auto" data-testid="agent-bdt-deposit-page">
      {/* Header */}
      <div className="mb-4 sm:mb-5 flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[2px] text-emerald-400 font-bold mb-1">
            Wallet · Top-up
          </div>
          <h1 className="text-[19px] sm:text-[26px] font-extrabold tracking-tight flex items-center gap-2">
            <Banknote className="text-emerald-300 flex-shrink-0" size={22} />
            BDT Deposit
          </h1>
          <p className="text-[11px] sm:text-[12px] text-white/55 mt-1 max-w-xl leading-snug">
            Pick a deposit channel and your mobile wallet. You&apos;ll be redirected to a secure gateway checkout — balance credits <b className="text-emerald-300">automatically</b>.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="agent-bdt-refresh"
          className="h-9 px-2.5 sm:px-3 inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] text-[11px] sm:text-[12px] text-white/70 hover:text-white hover:bg-white/[0.08] transition flex-shrink-0"
        >
          <RefreshCw size={12} /> <span className="hidden xs:inline sm:inline">Refresh</span>
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5 sm:mb-6">
        <StatTile label="Total credited"   value={fmtBdt(totalCredited)} icon={Wallet} color="#10b981" testid="agent-bdt-stat-total" />
        <StatTile label="Submissions"      value={history.length} icon={Receipt} color="#3b82f6" testid="agent-bdt-stat-count" />
        <StatTile label="Min per deposit"  value={effectiveMin > 0 ? fmtBdt(effectiveMin) : "—"} icon={CheckCircle2} color="#f59e0b" testid="agent-bdt-stat-min" />
        <StatTile label="Max per deposit"  value={effectiveMax > 0 ? fmtBdt(effectiveMax) : "—"} icon={CheckCircle2} color="#a855f7" testid="agent-bdt-stat-max" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5">
        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="agent-bdt-deposit-form"
          className="lg:col-span-3 rounded-2xl border border-white/10 bg-gradient-to-b from-[#0d1626] to-[#0a111e] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] p-3.5 sm:p-6"
        >
          {/* ---------- Step 1: Channel selection (Wallet) ---------- */}
          <SectionHeader index="01" title="Select deposit channel" subtitle="Choose the mobile wallet you'll pay with" />
          <div className="grid grid-cols-3 gap-2 sm:gap-2.5 mb-5 sm:mb-6">
            {(activeGateway?.methods || []).map((m) => {
              const mt = METHOD_THEME[m] || { color: "#10b981", label: m, hint: "" };
              const active = effectiveMethod === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  data-testid={`agent-bdt-method-${m}`}
                  className={`relative rounded-xl sm:rounded-2xl p-2.5 sm:p-4 text-left overflow-hidden transition-all border ${
                    active
                      ? "border-white/30"
                      : "border-white/[0.08] hover:border-white/20"
                  }`}
                  style={{
                    background: active
                      ? `linear-gradient(135deg, ${mt.color}1f 0%, ${mt.deep}10 100%)`
                      : "rgba(255,255,255,0.02)",
                    boxShadow: active
                      ? `0 0 0 1.5px ${mt.color}66, 0 18px 40px -22px ${mt.color}aa`
                      : "none",
                  }}
                >
                  {/* Selected check */}
                  {active && (
                    <span
                      className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 h-4 w-4 sm:h-5 sm:w-5 rounded-full grid place-items-center"
                      style={{ background: mt.color, color: "#fff" }}
                    >
                      <Check size={10} strokeWidth={3} />
                    </span>
                  )}

                  {/* Brand tile — responsive size */}
                  <div className="flex items-center justify-center mb-2 sm:mb-2.5" data-testid={`agent-bdt-method-logo-${m}`}>
                    <div className={active ? "scale-[1.06] transition-transform" : "opacity-95 transition"}>
                      {/* 48px on mobile (<sm), 64px from sm up */}
                      <span className="block sm:hidden"><WalletBrandLogo id={m} size={48} /></span>
                      <span className="hidden sm:block"><WalletBrandLogo id={m} size={64} /></span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-[11.5px] sm:text-[13px] font-extrabold tracking-tight text-white">
                      {mt.label}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-white/45 mt-0.5">
                      {mt.hint}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ---------- Step 2: Payment Gateway (Pro dropdown) ---------- */}
          <SectionHeader index="02" title="Select payment gateway" subtitle="Pick the PSP that will process your payment" />
          <div className="relative mb-6">
            <button
              type="button"
              onClick={() => setGatewayDropdownOpen((o) => !o)}
              data-testid="agent-bdt-gateway-trigger"
              className="w-full h-16 px-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] hover:border-white/20 transition-all flex items-center gap-3 text-left"
              style={
                gatewayDropdownOpen
                  ? { borderColor: `${activeProviderTheme.tone}66`, boxShadow: `0 0 0 1px ${activeProviderTheme.tone}33` }
                  : undefined
              }
            >
              <div
                className="h-10 w-10 rounded-lg grid place-items-center font-extrabold text-[14px] flex-shrink-0"
                style={{ background: `${activeProviderTheme.tone}22`, color: activeProviderTheme.tone }}
              >
                {(activeProviderTheme.short || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] font-extrabold text-white truncate">
                    {activeProviderTheme.short}
                  </span>
                  {activeGateway?.recommended && (
                    <span
                      className="inline-flex items-center gap-0.5 h-4 px-1.5 rounded text-[8.5px] font-bold uppercase tracking-wider"
                      style={{ background: `${activeProviderTheme.tone}22`, color: activeProviderTheme.tone }}
                    >
                      <Star size={9} /> Recommended
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] text-white/45 mt-0.5 truncate">
                  {activeProviderTheme.tagline} · supports{" "}
                  {(activeGateway?.methods || [])
                    .map((mm) => METHOD_THEME[mm]?.label || mm)
                    .join(" / ")}
                </div>
              </div>
              <ChevronDown
                size={18}
                className={`text-white/45 transition-transform ${gatewayDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {gatewayDropdownOpen && (
              <div
                className="absolute z-30 left-0 right-0 mt-2 rounded-xl border border-white/10 bg-[#0e1828] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden"
                data-testid="agent-bdt-gateway-list"
              >
                {(cfg.gateways || []).map((g) => {
                  const t = PROVIDER_THEME[g.provider] || { tone: "#10b981", short: g.provider, tagline: "" };
                  const active = effectiveGatewayId === g.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setGatewayId(g.id);
                        setGatewayDropdownOpen(false);
                      }}
                      data-testid={`agent-bdt-gateway-${g.provider}`}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/[0.04] border-b border-white/[0.04] last:border-0 transition"
                    >
                      <div
                        className="h-9 w-9 rounded-lg grid place-items-center font-extrabold text-[13px] flex-shrink-0"
                        style={{ background: `${t.tone}22`, color: t.tone }}
                      >
                        {t.short.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[13px] font-extrabold text-white">{t.short}</span>
                          {g.recommended && (
                            <span
                              className="inline-flex items-center gap-0.5 h-4 px-1.5 rounded text-[8px] font-bold uppercase tracking-wider"
                              style={{ background: `${t.tone}22`, color: t.tone }}
                            >
                              <Star size={8} /> Recommended
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-white/45 mt-0.5">
                          {t.tagline} · {(g.methods || []).map((mm) => METHOD_THEME[mm]?.label || mm).join(" / ")}
                        </div>
                      </div>
                      {active && (
                        <span
                          className="h-5 w-5 rounded-full grid place-items-center flex-shrink-0"
                          style={{ background: t.tone, color: "#fff" }}
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---------- Step 3: Amount ---------- */}
          <SectionHeader index="03" title="Enter amount" subtitle={`Between ${effectiveMin > 0 ? fmtBdt(effectiveMin) : "—"} and ${effectiveMax > 0 ? fmtBdt(effectiveMax) : "∞"}`} />
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50 text-[18px] font-extrabold tabular-nums">৳</span>
            <input
              type="number"
              inputMode="numeric"
              min={effectiveMin || undefined}
              max={effectiveMax || undefined}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`${effectiveMin || 100}–${effectiveMax > 0 ? effectiveMax : "∞"}`}
              className="w-full h-12 sm:h-14 pl-9 pr-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-emerald-400/60 outline-none text-[16px] sm:text-[17px] font-bold text-white placeholder-white/30 transition tabular-nums"
              data-testid="agent-bdt-input-amount"
            />
          </div>

          {/* Quick amount chips */}
          <div className="mt-2.5 flex flex-wrap gap-1.5 sm:gap-2">
            {[500, 1000, 2000, 5000, 10000, 20000].map((v) => {
              if (effectiveMin > v) return null;
              if (effectiveMax > 0 && v > effectiveMax) return null;
              const isActive = String(amount) === String(v);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(String(v))}
                  data-testid={`agent-bdt-quick-${v}`}
                  className={`h-8 px-2.5 sm:px-3 rounded-md text-[11px] sm:text-[11.5px] font-bold border transition tabular-nums ${
                    isActive
                      ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-300"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/20 hover:text-white"
                  }`}
                >
                  ৳{v.toLocaleString()}
                </button>
              );
            })}
          </div>

          {/* Trust strip */}
          <div className="mt-5 grid grid-cols-3 gap-1.5 sm:gap-2 text-[9.5px] sm:text-[10px] text-white/55">
            <TrustChip Icon={ShieldCheck} text="PSP encrypted" />
            <TrustChip Icon={Zap}         text="Instant credit" />
            <TrustChip Icon={CheckCircle2} text="Auto-confirm" />
          </div>

          {cfg.notice && (
            <div className="mt-3 text-[11px] text-white/55 leading-relaxed bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2">
              {cfg.notice}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="agent-bdt-submit"
            className="mt-5 w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl font-extrabold text-[13px] sm:text-[14px] transition-all disabled:opacity-40 disabled:cursor-not-allowed px-3"
            style={{
              background: canSubmit
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "rgba(255,255,255,0.06)",
              color: canSubmit ? "#04130a" : "rgba(255,255,255,0.5)",
              boxShadow: canSubmit ? "0 12px 28px -10px rgba(16,185,129,0.55)" : "none",
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin flex-shrink-0" /> <span className="truncate">Creating order…</span>
              </>
            ) : (
              <>
                <ExternalLink size={15} className="flex-shrink-0" />
                <span className="truncate">
                  Pay {amount ? fmtBdt(amount) : ""} via {activeProviderTheme.short}
                </span>
              </>
            )}
          </button>
        </form>

        {/* History panel */}
        <div
          className="lg:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-b from-[#0d1626] to-[#0a111e] p-3.5 sm:p-5 flex flex-col"
          data-testid="agent-bdt-history"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[2px] text-white/45 font-bold">Recent</div>
              <div className="font-bold text-[15px]">Submissions</div>
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              <Filter size={11} className="text-white/40" />
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                className="bg-white/[0.04] border border-white/10 rounded-md px-2 py-1 text-[11px] text-white/80 outline-none focus:border-emerald-400/60"
                data-testid="agent-bdt-history-filter"
              >
                <option value="">All wallets</option>
                <option value="bkash">bKash</option>
                <option value="nagad">Nagad</option>
                <option value="rocket">Rocket</option>
              </select>
            </div>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-[12px] text-white/40 py-10">
              No submissions yet.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto max-h-[520px] -mx-2 px-2 space-y-2 custom-scroll">
              {filteredHistory.map((h) => {
                const ch = h.gateway_method || h.channel || "bkash";
                const t = METHOD_THEME[ch] || { color: "#10b981", label: ch, logo: "" };
                const stKey = h.status || "pending";
                const st = STATUS_PILL[stKey] || STATUS_PILL.pending;
                const StIcon = st.icon;
                const provTone = PROVIDER_THEME[h.provider]?.tone || "#10b981";
                const provShort = PROVIDER_THEME[h.provider]?.short || h.provider || "—";
                return (
                  <div
                    key={h.id}
                    className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 hover:bg-white/[0.04] transition"
                    data-testid={`agent-bdt-history-row-${h.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <WalletBrandLogo id={ch} size={36} />
                        <div className="min-w-0">
                          <div className="text-[12px] font-extrabold text-white leading-tight truncate">{t.label}</div>
                          <div className="text-[9.5px] text-white/45 mt-0.5">
                            via{" "}
                            <span className="font-bold" style={{ color: provTone }}>
                              {provShort}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className="font-mono font-extrabold text-[13px] tabular-nums leading-tight"
                          style={{ color: stKey === "completed" ? "#10b981" : "#fff" }}
                        >
                          {stKey === "completed" ? "+" : ""}
                          {fmtBdt(h.credited_amount || h.amount)}
                        </div>
                        <span
                          className="inline-flex items-center gap-1 h-4 px-1.5 mt-1 rounded text-[9px] font-bold uppercase tracking-wider"
                          style={{ background: `${st.color}22`, color: st.color }}
                        >
                          <StIcon size={9} /> {st.label}
                        </span>
                      </div>
                    </div>
                    <div className="text-[10.5px] text-white/55 space-y-0.5">
                      {h.reference && (
                        <div className="truncate">
                          REF:{" "}
                          <span className="font-mono text-white/80">{h.reference}</span>
                        </div>
                      )}
                      {stKey === "pending" && h.redirect_url && (
                        <a
                          href={h.redirect_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"
                          data-testid={`agent-bdt-history-resume-${h.id}`}
                        >
                          <ExternalLink size={10} /> Resume payment
                        </a>
                      )}
                      <div className="text-white/40">{fmtDate(h.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 5px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }
      `}</style>
    </div>
  );
}

/* ---- Section header with step badge ---- */
function SectionHeader({ index, title, subtitle }) {
  return (
    <div className="flex items-baseline gap-2 mb-2.5">
      <span className="inline-flex items-center justify-center h-5 min-w-[24px] px-1.5 rounded-md text-[9.5px] font-extrabold bg-emerald-500/15 text-emerald-300 tabular-nums">
        {index}
      </span>
      <div>
        <div className="text-[12px] sm:text-[13px] font-extrabold text-white tracking-tight">
          {title}
        </div>
        {subtitle && (
          <div className="text-[10px] text-white/45 mt-0.5">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function TrustChip({ Icon, text }) {
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 flex items-center gap-1.5">
      <Icon size={11} className="text-emerald-400 flex-shrink-0" />
      <span className="text-white/65 font-bold truncate">{text}</span>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, color, testid }) {
  return (
    <div
      className="rounded-xl p-2.5 sm:p-3 border border-white/[0.08] min-w-0"
      style={{ background: `linear-gradient(135deg, ${color}14 0%, rgba(255,255,255,0.02) 100%)` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 sm:gap-2">
        <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${color}22`, color }}>
          <Icon size={12} />
        </div>
        <div className="text-[8.5px] sm:text-[9.5px] uppercase tracking-[1.2px] sm:tracking-[1.5px] font-bold text-white/55 truncate">{label}</div>
      </div>
      <div className="mt-1.5 sm:mt-2 font-mono font-extrabold text-[13px] sm:text-[17px] text-white tabular-nums truncate">{value}</div>
    </div>
  );
}
